import axios from 'axios';
import Cookies from 'js-cookie';
import AuthService from './AuthService';

const BASE_URL =  'https://audit-ai.net/api/'

//const BASE_URL =  'http://localhost:8000/api/';  
// Create axios instance with dynamic token retrieval
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Create a public axios instance without authentication
export const publicAxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor to dynamically add the current token
axiosInstance.interceptors.request.use(
  config => {
    // Get the latest token from multiple sources
    let token = AuthService.getToken();
    
    // If no token from AuthService, check localStorage directly
    if (!token) {
      token = localStorage.getItem('token');
      console.log(`AxiosService: Using token from localStorage: ${token ? 'present' : 'missing'}`);
    }
    
    // If still no token, check Cookies
    if (!token) {
      token = Cookies.get('token');
      console.log(`AxiosService: Using token from Cookies: ${token ? 'present' : 'missing'}`);
    }
    
    const username = AuthService.getUsername();
    
    console.log(`AxiosService: Making API request to: ${config.baseURL}${config.url}`);
    console.log(`AxiosService: Request method: ${config.method}`);
    console.log(`AxiosService: Token: ${token ? 'present' : 'missing'}`);
    console.log(`AxiosService: Authenticated username: ${username || 'unknown'}`);
    
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error('AxiosService: Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Function to refresh token
const refreshToken = async () => {
    try {
        console.log('AxiosService: Attempting to refresh token with refresh token');
        // Use AuthService instead of direct axios call
        return await AuthService.refreshToken();
    } catch (error) {
        console.error('AxiosService: Error refreshing token:', error);
        return Promise.reject(error);
    }
};

// Function to handle auth failures by redirecting to login
const forceLogout = (errorMessage) => {
  console.error('Authentication error:', errorMessage);
  
  // Clear all auth data
  AuthService.logout();
  
  // Display message to user (optional)
  const message = 'Your session has expired. Please log in again.';
  
  // Redirect to login page with error message
  window.location.href = `/loginform?error=${encodeURIComponent(message)}`;
};
  
// Axios interceptor for token refresh
axiosInstance.interceptors.response.use(
  response => {
    console.log(`AxiosService: Response received from: ${response.config.url}`, {
      status: response.status,
      statusText: response.statusText,
      data: response.data ? (typeof response.data === 'object' ? 'object' : response.data) : 'no data'
    });
    return response;
  },
  async error => {
    console.error(`AxiosService: Response error from: ${error.config?.url || 'unknown URL'}`, {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data || 'no response data'
    });

    const originalRequest = error.config;

    // Check for specific token validation error
    const errorDetail = error.response?.data?.detail || '';
    
    if (
      // Token validation error from Django REST
      (typeof errorDetail === 'string' && 
       errorDetail.includes('Given token not valid for any token type')) ||
      // General authentication errors
      (error.response?.status === 401 && !originalRequest._retry)
    ) {
      // If it's specifically a token validation error, force logout
      if (errorDetail.includes('Given token not valid for any token type')) {
        forceLogout('Token validation failed: ' + errorDetail);
        return Promise.reject(error);
      }
      
      // For other 401 errors, try token refresh first
      originalRequest._retry = true;

      try {
        const token = await refreshToken();
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Handle refresh token failure by forcing logout
        forceLogout('Failed to refresh token');
        return Promise.reject(refreshError);
      }
    }
    
    // Handle 403 Forbidden errors (permissions issues)
    if (error.response?.status === 403) {
      console.error('Permission denied:', error.response.data);
    }
    
    return Promise.reject(error);
  }
);

// Simple response handler for the public instance
publicAxiosInstance.interceptors.response.use(
  response => response,
  error => {
    console.error('Public API error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export default axiosInstance;
